'use client'

/**
 * The Meet step's editor inside the draft card (#16814, part of #16812).
 *
 * Two controls over one `agenda` draft:
 *
 *  - **Meeting** — a radio group over the asker's upcoming meetings whose
 *    agenda they may edit, as the turn found them; the draft arrives on the
 *    soonest. Choosing another retargets the draft. The server does not
 *    trust the choice: Approve re-resolves the meeting in the session's org
 *    and re-runs the edit gate.
 *  - **Agenda items** — a `ReorderableList` of text fields: edit in place,
 *    Add item, and Remove / reorder from each row's menu or by keyboard drag.
 *
 * Rows carry a local id so a reorder or a removal never re-keys the field
 * someone is typing in; the draft itself carries only the strings, in order.
 * Plain text in form controls, semantic tokens only.
 */

import { useRef, useState } from 'react'

import { Plus } from '@/components/icons'
import { ReorderableList } from '@/components/reorderable-list'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RadioCardGroup } from '@/components/ui/radio-card-group'
import {
  type AgendaDraft,
  type Draft,
  MAX_AGENDA_ITEM_LENGTH,
  MAX_AGENDA_ITEMS,
} from '@/lib/ai-chat/next-steps'

interface ItemRow {
  id: string
  text: string
}

export function AgendaDraftEditor({
  draft,
  disabled,
  onChange,
}: {
  draft: AgendaDraft
  disabled: boolean
  onChange: (draft: Draft) => void
}) {
  const nextId = useRef(0)
  const newRow = (text: string): ItemRow => {
    nextId.current += 1
    return { id: `item-${nextId.current}`, text }
  }
  const [rows, setRows] = useState<ItemRow[]>(() => draft.items.map(newRow))
  // The row Add item just made. Its field takes focus when it MOUNTS — a
  // ref callback rather than an effect, because the list adopts new items
  // in a render of its own, after this component's effects have run.
  const focusOnMount = useRef<string | null>(null)

  function commit(next: ItemRow[]) {
    setRows(next)
    onChange({ ...draft, items: next.map(row => row.text) })
  }

  function pickMeeting(meetingId: string) {
    const meeting = draft.meetings.find(choice => choice.id === meetingId)
    if (!meeting) return
    onChange({ ...draft, meetingId: meeting.id, meetingTitle: meeting.title })
  }

  function addItem() {
    const row = newRow('')
    focusOnMount.current = row.id
    commit([...rows, row])
  }

  return (
    <div data-testid="ai-chat-draft-agenda" className="mt-3 space-y-3">
      {draft.meetings.length > 0 && (
        <RadioCardGroup
          legend="Meeting"
          value={draft.meetingId}
          disabled={disabled}
          onChange={pickMeeting}
          options={draft.meetings.map(meeting => ({
            value: meeting.id,
            label: meeting.title,
            description: meeting.when,
          }))}
        />
      )}

      <div className="space-y-2">
        <p className="text-sm font-medium text-text-primary">Agenda items</p>
        <ReorderableList
          items={rows}
          getId={row => row.id}
          getLabel={row =>
            row.text.trim() ||
            `item ${rows.findIndex(r => r.id === row.id) + 1}`
          }
          onReorder={commit}
          onRemove={row => commit(rows.filter(r => r.id !== row.id))}
          disabled={disabled}
          listLabel="Agenda items"
          className="space-y-1"
          data-testid="ai-chat-draft-agenda-items"
          renderItem={(row, index) => (
            <Input
              aria-label={`Agenda item ${index + 1}`}
              value={row.text}
              maxLength={MAX_AGENDA_ITEM_LENGTH}
              disabled={disabled}
              ref={element => {
                if (element && focusOnMount.current === row.id) {
                  focusOnMount.current = null
                  element.focus()
                }
              }}
              onChange={event =>
                commit(
                  rows.map(r =>
                    r.id === row.id ? { ...r, text: event.target.value } : r,
                  ),
                )
              }
            />
          )}
        />
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="pointer-coarse:min-h-11"
          disabled={disabled || rows.length >= MAX_AGENDA_ITEMS}
          onClick={addItem}
        >
          <Plus aria-hidden="true" className="h-4 w-4" />
          Add item
        </Button>
      </div>
    </div>
  )
}
